import { FC, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { IntroducingBrokerDetailsDto } from '../../../model/IntroducingBrokerDetailsDto'
import { LoyaltyDashboardDetailsDto } from '../../../model/IntroducingBrokerLoyalty'
import { calculatePercentage, isNonNegativeNumber } from '../../../utils/numbers.utils'
import { ProgressBarsBlock } from '../components/ProgressBarsBlock/ProgressBarsBlock'

interface ProgressBarsProps {
  loyaltyData?: LoyaltyDashboardDetailsDto
  ibData?: IntroducingBrokerDetailsDto
}

const calculateBlockPercentage = (
  overallCurrent: number,
  prevRequired: number,
  currentRequired: number
) => {
  if (overallCurrent >= currentRequired) {
    return 100
  }
  if (overallCurrent <= prevRequired) {
    return 0
  }

  return ((overallCurrent - prevRequired) / (currentRequired - prevRequired)) * 100
}

export const ProgressBars: FC<ProgressBarsProps> = (props) => {
  const { loyaltyData, ibData } = props

  const { t } = useTranslation()

  const getLabel = useCallback(
    (singularKey: string, amount?: number, pluralKey?: string) => {
      if (isNonNegativeNumber(amount)) {
        if (pluralKey) {
          return amount === 1 ? `${amount} ${t(singularKey)}` : `${amount} ${t(pluralKey)}`
        }
        return `${amount} ${t(singularKey)}`
      }
      return ''
    },
    [t]
  )

  const currentTier = ibData?.loyaltyDetails?.currentTier
  const currentTierMinLots = currentTier?.minLots ?? 0
  const currentTierMaxLots = currentTier?.maxLots ?? 0
  const currentTierLots = currentTier?.lots.current ?? 0
  const nextTier = ibData?.loyaltyDetails?.nextTier

  const lotsPercentages = useMemo(() => {
    const loyaltyTiers = ibData?.loyaltyDetails?.tiers

    const getTierWithSubtiersBeforeCurrent = () => {
      // Complete tier like Bronze doesn't have sub-tiers although should they should be used if previous tier required value equals current lots value. Otherwise will show single bar without 4 slices.
      // Once will have at least 1 lot higher than previous tier then sub-tiers are not required anymore.
      const tierIndexWithSubTiersBeforeCurrent =
        loyaltyTiers?.findIndex((tier, idx) => {
          const previousTier = loyaltyTiers?.[idx - 1]

          // Only used if next tier was started but current lots didn't exceed previous tier required lots
          const doesTierBeforeCurrentTierHasSubTiers =
            tier.lots.current === previousTier?.lots.required &&
            (previousTier?.subTiers?.length ?? 0) > 0

          return doesTierBeforeCurrentTierHasSubTiers
        }) ?? -1

      const tierWithSubTiersBeforeCurrent =
        tierIndexWithSubTiersBeforeCurrent > 0
          ? loyaltyTiers?.[tierIndexWithSubTiersBeforeCurrent - 1]
          : undefined

      return tierWithSubTiersBeforeCurrent
    }

    const subTiers = currentTier?.subTiers ?? getTierWithSubtiersBeforeCurrent()?.subTiers

    if (subTiers) {
      let ignoreRestCalculations = false

      return (
        subTiers?.map((tier, idx) => {
          let currentLots
          let requiredLots

          if (!tier.isCurrent) {
            currentLots = tier.lots.current
            requiredLots = tier?.lots.required
          } else if (!nextTier) {
            currentLots = currentTierLots
            requiredLots = currentTierMaxLots
          } else {
            currentLots = currentTierLots - currentTierMinLots
            requiredLots = currentTierMaxLots - currentTierMinLots
          }

          const percent = !ignoreRestCalculations
            ? calculatePercentage(currentLots, requiredLots)
            : null

          if (tier.isCurrent || tier.lots.current === tier.lots.required) {
            ignoreRestCalculations = true
          }

          return {
            percent,
            isCurrent: tier.isCurrent,
            isNextStarted: subTiers[idx + 1]?.lots.current > subTiers[idx + 1]?.minLots,
          }
        }) ?? []
      )
    }

    const currentLots = !nextTier ? currentTierLots : currentTierLots - currentTierMinLots
    const requiredLots = !nextTier ? currentTierMaxLots : currentTierMaxLots - currentTierMinLots

    return [
      {
        percent: calculatePercentage(currentLots, requiredLots),
        isCurrent: true,
      },
    ]
  }, [
    currentTier?.subTiers,
    currentTierLots,
    currentTierMaxLots,
    currentTierMinLots,
    ibData?.loyaltyDetails?.tiers,
    nextTier,
  ])

  const items = useMemo(() => {
    const activeClientsPercent =
      loyaltyData?.activeClients && loyaltyData?.activeClients.required
        ? calculateBlockPercentage(
            loyaltyData.activeClients.current,
            0,
            loyaltyData.activeClients.required
          )
        : 0

    const volumeRatioPercent =
      loyaltyData?.volumeRatio && loyaltyData?.volumeRatio.required
        ? calculateBlockPercentage(
            loyaltyData.volumeRatio.current,
            0,
            loyaltyData.volumeRatio.required
          )
        : 0

    return [
      {
        title: t('IB.Dashboard.Lots'),
        isExtraInfoItem: true,
        label: getLabel('IB.Dashboard.Lot', currentTier?.lots?.required, 'IB.Dashboard.Lots'),
        progressBarTooltip: getLabel(
          'IB.Dashboard.Lot',
          currentTier?.lots?.current ?? 0,
          'IB.Dashboard.Lots'
        ),
        sections: lotsPercentages,
      },
      {
        title: t('IB.Dashboard.Active Clients (90 Days)'),
        label: getLabel(
          'IB.Dashboard.Client',
          loyaltyData?.activeClients.required,
          'IB.Dashboard.Clients'
        ),
        progressBarTooltip: getLabel(
          'IB.Dashboard.Client',
          loyaltyData?.activeClients.current,
          'IB.Dashboard.Clients'
        ),
        sections: [
          {
            percent: activeClientsPercent,
            isCurrent: true,
          },
        ],
      },
      {
        title: t('IB.Dashboard.Volume'),
        label: `${loyaltyData?.volumeRatio.required}% ${t('IB.Dashboard.Volume')}`,
        progressBarTooltip: `${loyaltyData?.volumeRatio.current}% ${t('IB.Dashboard.Volume')}`,
        sections: [
          {
            percent: volumeRatioPercent,
            isCurrent: true,
          },
        ],
      },
    ]
  }, [loyaltyData, t, getLabel, lotsPercentages, currentTier?.lots])

  const { current, next } = loyaltyData?.tiers || {}
  const { subTiers, id } = current || {}

  let tierRewardAmount = next?.amount

  if (subTiers?.length) {
    const currentSubTierIndex = subTiers.findIndex(({ id: subTierId }) => subTierId === id)
    const nextSubTierAmount = subTiers[currentSubTierIndex + 1]?.amount

    if (nextSubTierAmount) {
      tierRewardAmount = nextSubTierAmount
    }
  }

  const isFinalTier = loyaltyData?.tiers.final?.id === id

  const prizeText = tierRewardAmount
    ? `${getLabel('IB.Dashboard.Lot', currentTier?.lots?.required, 'IB.Dashboard.Lots')} - $${t(
        'Loyalty Program.amount_prize',
        {
          amount: tierRewardAmount,
        }
      )}`
    : null

  return <ProgressBarsBlock items={items} prize={isFinalTier ? null : prizeText} />
}
